import { FieldProps } from 'formik'
import { noop } from 'lodash-es'
import { ITag, ITagPickerProps, Label, TagPicker } from 'office-ui-fabric-react'
import * as React from 'react'

export type FormikTagPickerProps = FieldProps<any> & {
  tags: ITag[]
  label?: string
  required?: boolean
  defaultSelectedItem?: ITag
  suggestionLimit?: number
  suggestionsHeaderText?: string
  itemLimit?: number
  /**
   * Callback that receives the last added item
   */
  onChange?: (item: ITag) => void
  /**
   * Same as onChange, for interchangeability with FormikDropdown
   */
  onChanged?: (item: ITag) => void
} & Partial<
    Omit<
      ITagPickerProps,
      'onItemSelected' | 'onChange' | 'defaultSelectedItems' | 'itemLimit'
    >
  >

export type TagPickerCustomProps = Omit<FormikTagPickerProps, 'field' | 'form'>

export class FormikArrayTagPicker extends React.Component<FormikTagPickerProps> {
  private tags: ITag[] = []
  private defaultItems: ITag[] | undefined
  constructor(props: FormikTagPickerProps) {
    super(props)

    this._createOptions(props)
    this._setDefaultItems(props)
  }

  public componentWillReceiveProps(nextProps: FormikTagPickerProps) {
    this._createOptions(nextProps)
  }

  public render(): JSX.Element {
    return (
      <>
        {this.props.label && (
          <Label required={this.props.required}>{this.props.label}</Label>
        )}

        <TagPicker
          {...this.props}
          onChange={(items) => {
            if (!items || items.length === 0) {
              // @ts-ignore
              this.props.form.setFieldValue(this.props.field.name, [])
            } else {
              const values = items.map((item) => item.key)
              // @ts-ignore
              this.props.form.setFieldValue(this.props.field.name, values)
            }
          }} // disable normal onChange
          pickerSuggestionsProps={{
            suggestionsHeaderText: this.props.suggestionsHeaderText,
          }}
          itemLimit={this.props.itemLimit || 1}
          onEmptyInputFocus={this._onEmptyInputFocus(
            this.tags,
            this.props.suggestionLimit
          )}
          onResolveSuggestions={
            this.props.onResolveSuggestions ||
            this._onFilterChanged(this.props.suggestionLimit)
          }
          defaultSelectedItems={this.defaultItems}
          onItemSelected={(item) => {
            if (item) {
              if (this.props.onChange) {
                this.props.onChange(item)
              }
              if (this.props.onChanged) {
                this.props.onChanged(item)
              }
              return item
            }

            return null
          }}
        />
      </>
    )
  }

  private _createOptions(props: FormikTagPickerProps) {
    this.tags = props.tags.filter((tag) => {
      const matches = props.field.value.filter((key: string) => tag.key === key)
      return matches.length === 0 ? true : false
    })
  }

  private _setDefaultItems(props: FormikTagPickerProps) {
    if (props.defaultSelectedItem) {
      this.defaultItems = [props.defaultSelectedItem]
    }
    if (props.field.value.length === 0) {
      this.defaultItems = this.tags.filter((item) => item.key === '')
    } else {
      const tagsWithoutGeneral = this.tags.filter((tag) => tag.key !== '')
      this.defaultItems = tagsWithoutGeneral.filter((cleanTag) => {
        const matches = props.field.value.filter(
          (key: string) => cleanTag.key === key
        )
        return matches.length === 0 ? false : true
      })
    }
  }

  private _onFilterChanged(limit: number | undefined) {
    return (filterText: string) => {
      let filteredTags = this.tags
      if (filterText) {
        filteredTags = this.tags.filter(
          (tag) =>
            `${tag.name.toLowerCase()}${tag.key.toLowerCase()}`.indexOf(
              filterText.toLowerCase()
            ) !== -1
        )
      }
      return filteredTags.slice(0, limit || 500)
    }
  }

  private _onEmptyInputFocus(tags: ITag[], limit: number | undefined) {
    if (limit) {
      return () => tags.slice(0, limit)
    }
    return () => tags
  }
}

// This is the above picker without formik stuff, that can easily replace dropdowns
export class TagPickerCustom extends React.Component<TagPickerCustomProps> {
  private tags: ITag[] = []
  private defaultItems: ITag[] | undefined

  constructor(props: FormikTagPickerProps) {
    super(props)
    this._createOptions(props)
  }

  public componentWillReceiveProps(nextProps: FormikTagPickerProps) {
    this._createOptions(nextProps)
  }

  public render(): JSX.Element {
    return (
      <>
        {this.props.label && (
          <Label required={this.props.required}>{this.props.label}</Label>
        )}
        <TagPicker
          {...this.props}
          onChange={(_) => {
            noop()
          }} // disable normal onChange
          pickerSuggestionsProps={{
            suggestionsHeaderText: this.props.suggestionsHeaderText,
          }}
          itemLimit={this.props.itemLimit || 1}
          onEmptyInputFocus={this._onEmptyInputFocus(
            this.tags,
            this.props.suggestionLimit
          )}
          onResolveSuggestions={this._onFilterChanged(
            this.props.suggestionLimit
          )}
          defaultSelectedItems={this.defaultItems}
          onItemSelected={(item) => {
            if (item) {
              if (this.props.onChange) {
                this.props.onChange(item)
              }
              if (this.props.onChanged) {
                this.props.onChanged(item)
              }
              return item
            }
            return null
          }}
        />
      </>
    )
  }

  private _createOptions(props: FormikTagPickerProps) {
    this.tags = props.tags
  }

  private _onFilterChanged(limit: number | undefined) {
    return (filterText: string) =>
      filterText
        ? this.tags.filter(
            (tag) =>
              tag.name.toLowerCase().indexOf(filterText.toLowerCase()) !== -1
          )
        : this.tags.slice(0, limit)
  }

  private _onEmptyInputFocus(tags: ITag[], limit: number | undefined) {
    if (limit) {
      return () => tags.slice(0, limit)
    }
    return () => tags
  }
}
